import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Plus,
  Trash2,
  ChevronRight,
  ChevronDown,
  Calendar,
  ZoomIn,
  ZoomOut,
  CheckSquare,
  Square,
  RefreshCw,
} from "lucide-react";
import { taskApi } from "api/tasks/taskApi"; // <-- adjust path if needed
import Xarrow from "react-xarrows";
import clsx from "clsx";
/**
 * Custom Gantt component (no external gantt libraries)
 * - 3 levels: Task Group → Task → Subtask
 * - Zoomable timeline (px/day)
 * - Drag / resize tasks & subtasks; updates persisted via taskApi
 * - Subtask checkbox to toggle completion
 * - Left table columns: Name, Status, Due Date
 * - Tailwind CSS only
 * - Uses SVG for timeline
 */

// --- Helpers ---
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const parseISODateLocal = (s) => {
  // Treat API YYYY-MM-DD as local date at midnight
  if (!s) return new Date();
  return new Date(`${s}T00:00:00`);
};
const formatISODate = (d) => {
  if (!(d instanceof Date)) d = new Date(d);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const daysBetween = (start, end) =>
  Math.max(1, Math.round((end - start) / MS_PER_DAY) + 1);

const getTaskProgress = (status) => {
  if (!status) return 0;
  const s = String(status).toLowerCase();
  if (s.includes("complete")) return 100;
  if (s.includes("ongoing") || s.includes("in progress")) return 50;
  return 0; // Not Started / others
};

const calculateGroupProgress = (summary) => {
  if (!summary || !summary.total_tasks) return 0;
  const { total_tasks, completed_tasks = 0 } = summary;
  return Math.round((completed_tasks / total_tasks) * 100);
};

const statusColor = (status) => {
  const s = String(status || "").toLowerCase();
  if (s.includes("complete"))
    return "bg-green-100 text-green-700 border-green-200";
  if (s.includes("ongoing") || s.includes("in progress"))
    return "bg-blue-100 text-blue-700 border-blue-200";
  if (s.includes("overdue")) return "bg-red-100 text-red-700 border-red-200";
  return "bg-gray-100 text-gray-700 border-gray-200"; // Not started / unknown
};

// --- Data shapes in this component ---
// item = {
//   id: string,                // "group_1" | "task_123" | "subtask_999"
//   label: string,
//   type: 'group'|'task'|'subtask',
//   parentId?: string,
//   start: Date,
//   end: Date,
//   status?: string,
//   progress?: number,         // 0..100
//   details: any,              // holds API "details"
//   expanded?: boolean,
// }
const StatusBadge = ({ value }) => {
  const cls =
    value === "Ongoing" || value === "Medium"
      ? " text-yellow-800 bg-yellow-100 "
      : value === "Completed" ||
          value === "Complete" ||
          value === "Low" ||
          value === "Active"
        ? "  text-green-800 bg-green-100"
        : value === "Overdue" || value === "High" || value === "Halted"
          ? " text-red-800 bg-red-100"
          : " text-gray-800 bg-gray-100";
  return (
    <span
      className={clsx(
        `{px-4 py-1 rounded-md items-center w-full font-normal text-sm flex flex-row justify-center hover:bg-gray-200 text-white}`,
        cls,
      )}
      style={{
        gap: "0.5rem",
      }}
    >
      {/* <span
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: colorMapping[value] || "#78797A" }}
        /> */}
      {value}
    </span>
  );
};

export default function CustomGantt() {
  const [items, setItems] = useState({}); // map id->item
  const [rootIds, setRootIds] = useState([]); // group ids only
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Zoom state
  const [pxPerDay, setPxPerDay] = useState(24); // default zoom
  const minPxPerDay = 1;
  const maxPxPerDay = 64;

  // Selection and UI
  const [selectedId, setSelectedId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(null); // { type: 'group'|'task'|'subtask', parentId?: string }
  const [hasTaskAuthority, setHasTaskAuthority] = useState(false);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  // Scroll sync refs
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const didAutoFitRef = useRef(false);
  useEffect(() => {
    const l = leftRef.current;
    const r = rightRef.current;
    if (!l || !r) return;
    const syncL = () => {
      r.scrollTop = l.scrollTop;
    };
    const syncR = () => {
      l.scrollTop = r.scrollTop;
    };
    l.addEventListener("scroll", syncL);
    r.addEventListener("scroll", syncR);
    return () => {
      l.removeEventListener("scroll", syncL);
      r.removeEventListener("scroll", syncR);
    };
  }, []);

  // --- Load data from APIs ---
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const taskGroups = await taskApi.getAllTaskGroups();
      const map = {};
      const roots = [];

      taskGroups.map(async (group, idx) => {
        const gid = `group_${group.id}`;
        const groupItem = {
          id: gid,
          label: group.group_name,
          type: "group",
          parentId: null,
          taskGroupOrder: idx,
          previous_group_id: idx > 0 ? `group_${taskGroups[idx - 1].id}` : null,
          start: parseISODateLocal(group.start_date),
          end: parseISODateLocal(group.end_date),
          status: group.status || "Active",
          progress: calculateGroupProgress(group.task_group_summary),
          expanded: false,
          details: {
            type: "taskGroup",
            originalId: group.id,
            priority: group.priority,
            allocated_members: group.allocated_members,
          },
        };
        map[gid] = groupItem;
        roots.push(gid);

        // load tasks
        try {
          const tasks = await taskApi.getAllTasks(group.id);
          const taskHeaderItem = {
            id: `task_header_${group.id}`,
            label: "Tasks",
            type: "taskHeader",
            parentId: gid,
            start: parseISODateLocal(group.start_date),
            end: parseISODateLocal(group.end_date),
            status: "",
            progress: 0,
            expanded: false,
            details: {
              type: "task",
              originalId: null,
              priority: group.priority,
              allocated_members: group.allocated_members,
            },
          };
          map[`task_header_${group.id}`] = taskHeaderItem;
          tasks.map(async (t, idx) => {
            const tid = `task_${t.id}`;
            const taskItem = {
              id: tid,
              label: t.task_name,
              type: "task",
              parentId: gid,
              start: parseISODateLocal(t.start_date),
              end: parseISODateLocal(t.end_date),
              status: t.task_status,
              progress: getTaskProgress(t.task_status),
              expanded: false,
              task_order: idx,
              previous_task_id: idx > 0 ? `task_${tasks[idx - 1].id}` : null,
              details: {
                type: "task",
                originalId: t.id,
                priority: t.priority,
                task_details: t.task_details,
                estimated_amount: t.estimated_amount,
                allocated_members: t.allocated_members,
                task_group: t.task_group,
              },
            };
            map[tid] = taskItem;

            // load subtasks
            try {
              const subtasks = await taskApi.getAllSubtasks(t.id);
              const subtaskHeaderItem = {
                id: `subtask_header_${t.id}`,
                label: "Subtasks",
                type: "subtaskHeader",
                parentId: tid,
                start: parseISODateLocal(t.start_date),
                end: parseISODateLocal(t.end_date),
                status: "",
                progress: 0,
                expanded: false,
                details: {
                  type: "subtask",
                  originalId: null,
                },
              };
              map[`subtask_header_${t.id}`] = subtaskHeaderItem;
              subtasks.map((s, idx) => {
                const sid = `subtask_${s.id}`;
                const subItem = {
                  id: sid,
                  label: s.subtask_name,
                  type: "subtask",
                  parentId: tid,
                  start: parseISODateLocal(s.start_date),
                  subtaskOrder: idx,
                  previous_subtask_id:
                    idx > 0 ? `subtask_${subtasks[idx - 1].id}` : null,
                  end: parseISODateLocal(s.end_date),
                  status: s.is_completed ? "Complete" : "Ongoing",
                  progress: s.is_completed ? 100 : 0,
                  expanded: false,
                  details: {
                    type: "subtask",
                    originalId: s.id,
                    is_completed: s.is_completed,
                    subtask_details: s.subtask_details,
                    task_id: s.task_id,
                    assign_to: s.assign_to,
                  },
                };
                map[sid] = subItem;
              });
            } catch (e) {
              console.warn("Failed subtasks for task", t.id, e);
            }
          });
        } catch (e) {
          console.warn("Failed tasks for group", group.id, e);
        }
      });
      setItems(map);
      setRootIds(roots);
    } catch (e) {
      console.error(e);
      setError("Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);
  useEffect(() => {
    const fetchAuthority = async () => {
      try {
        const siteData = JSON.parse(localStorage.getItem("siteData") || "{}");
        if (!siteData?.id) return;
        const response = await taskApi.getSiteMembers(siteData.id);
        if (response?.status) {
          const members = Array.isArray(response.data) ? response.data : [];
          const currentMember = members.find((m) => m.user_id === user.user);
          setHasTaskAuthority(
            currentMember?.create_taskgroup ||
              currentMember?.is_admin ||
              currentMember?.joined_as === "Owner",
          );
        }
      } catch {
        setHasTaskAuthority(false);
      }
    };
    fetchAuthority();
  }, [user.user]);

  // Reset auto-fit flag whenever a new load cycle starts
  useEffect(() => {
    if (loading) {
      didAutoFitRef.current = false;
    }
  }, [loading]);

  // Derived: flat visible rows (respect expanded)
  const rowOrder = useMemo(() => {
    const res = [];
    const pushChildren = (pid, level) => {
      const children = Object.values(items).filter((it) => it.parentId === pid);
      // stable order by start date, then name
      // children.sort(
      //   (a, b) => a.start - b.start || a.label.localeCompare(b.label)
      // );
      for (const c of children) {
        res.push({ id: c.id, level });
        if (c.expanded) pushChildren(c.id, level + 1);
      }
    };
    for (const gid of rootIds) {
      res.push({ id: gid, level: 0 });
      const g = items[gid];
      if (g?.expanded) pushChildren(gid, 1);
    }
    return res;
  }, [items, rootIds]);

  // Timeline bounds
  const { minDate, maxDate } = useMemo(() => {
    const all = Object.values(items);
    if (all.length === 0) {
      const today = new Date();
      const m = new Date(today.getFullYear(), today.getMonth(), 1);
      const M = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { minDate: m, maxDate: M };
    }
    let min = new Date(8640000000000000),
      max = new Date(-8640000000000000);
    for (const it of all) {
      if (it.start < min) min = it.start;
      if (it.end > max) max = it.end;
    }
    // pad by 60 days on each side
    // Pad to the start and end of the month, plus one extra month on each side
    min = new Date(min.getFullYear(), min.getMonth() - 1, 1);
    max = new Date(max.getFullYear(), max.getMonth() + 2, 0); // Last day of next month
    return { minDate: min, maxDate: max };
  }, [items]);

  const totalDays = useMemo(
    () => daysBetween(minDate, maxDate),
    [minDate, maxDate],
  );
  const contentWidth = useMemo(
    () => totalDays * pxPerDay,
    [totalDays, pxPerDay],
  );

  // --- Drag / Resize state ---
  const [dragState, setDragState] = useState(null); // { id, mode: 'move'|'resize-left'|'resize-right', startX, origStart, origEnd }

  // Mouse handlers for SVG bars
  // const onBarMouseDown = (e, id, mode) => {
  //   e.stopPropagation();
  //   const it = items[id];
  //   if (!it) return;
  const onBarMouseDown = (e, id, mode) => {
    e.stopPropagation();
    if (!hasTaskAuthority) return;
    const it = items[id];
    if (!it) return;
    let children = Object.values(items).filter(
      (it2) =>
        it2.parentId === it.id &&
        it2.type !== "subtaskHeader" &&
        it2.type !== "taskHeader",
    );
    for (const c of children) {
      c.origStart = new Date(c.start);
      c.origEnd = new Date(c.end);
    }
    let grandChildren = [];
    for (const c of children) {
      const gcs = Object.values(items).filter(
        (it2) =>
          it2.parentId === c.id &&
          it2.type !== "subtaskHeader" &&
          it2.type !== "taskHeader",
      );
      grandChildren.push(...gcs);
    }
    for (const c of grandChildren) {
      c.origStart = new Date(c.start);
      c.origEnd = new Date(c.end);
    }

    setDragState({
      id,
      mode,
      startX: e.clientX,
      origStart: new Date(it.start),
      origEnd: new Date(it.end),
      children: children.map((c) => ({
        id: c.id,
        origStart: c.origStart,
        origEnd: c.origEnd,
      })),
      grandChildren: grandChildren.map((c) => ({
        id: c.id,
        origStart: c.origStart,
        origEnd: c.origEnd,
      })),
    });
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragState) return;
      const dx = e.clientX - dragState.startX;
      const deltaDays = Math.round(dx / pxPerDay);
      const it = items[dragState.id];
      if (!it) return;
      let newStart = new Date(dragState.origStart);
      let newEnd = new Date(dragState.origEnd);
      const children = dragState.children || [];
      const grandChildren = dragState.grandChildren || [];
      let childToEdit = [];
      let grandChildToEdit = [];
      if (dragState.mode === "move") {
        newStart = new Date(
          dragState.origStart.getTime() + deltaDays * MS_PER_DAY,
        );
        newEnd = new Date(dragState.origEnd.getTime() + deltaDays * MS_PER_DAY);
        // move the children along with parent
        for (const c of children) {
          const childNewStart = new Date(
            c.origStart.getTime() + deltaDays * MS_PER_DAY,
          );
          const childNewEnd = new Date(
            c.origEnd.getTime() + deltaDays * MS_PER_DAY,
          );
          childToEdit.push({
            id: c.id,
            start: childNewStart,
            end: childNewEnd,
          });
        }
        for (const c of grandChildren) {
          const grandChildNewStart = new Date(
            c.origStart.getTime() + deltaDays * MS_PER_DAY,
          );
          const grandChildNewEnd = new Date(
            c.origEnd.getTime() + deltaDays * MS_PER_DAY,
          );
          grandChildToEdit.push({
            id: c.id,
            start: grandChildNewStart,
            end: grandChildNewEnd,
          });
        }
      } else if (dragState.mode === "resize-left") {
        newStart = new Date(
          dragState.origStart.getTime() + deltaDays * MS_PER_DAY,
        );
        if (newStart > newEnd)
          newStart = new Date(newEnd.getTime() - MS_PER_DAY);
      } else if (dragState.mode === "resize-right") {
        newEnd = new Date(dragState.origEnd.getTime() + deltaDays * MS_PER_DAY);
        if (newEnd < newStart)
          newEnd = new Date(newStart.getTime() + MS_PER_DAY);
      }
      // optional constraints: keep inside parent range
      const parent = it.parentId ? items[it.parentId] : null;
      let parentStart = parent ? parent.start : null;
      let parentEnd = parent ? parent.end : null;
      // if (parent) {
      //   if (newStart < parent.start) newStart = new Date(parent.start);
      //   if (newEnd > parent.end) newEnd = new Date(parent.end);
      // }
      if (parent) {
        if (newStart < parent.start) parentStart = new Date(newStart);
        if (newEnd > parent.end) parentEnd = new Date(newEnd);
        const childEarliestStart = Object.values(items).reduce((min, item) => {
          if (
            item.type !== "subtaskHeader" &&
            item.type !== "taskHeader" &&
            item.parentId === parent.id &&
            item.start < min
          ) {
            return item.start;
          }
          return min;
        }, new Date(8640000000000000));
        const childLatestEnd = Object.values(items).reduce((max, item) => {
          if (
            item.type !== "subtaskHeader" &&
            item.type !== "taskHeader" &&
            item.parentId === parent.id &&
            item.end > max
          ) {
            return item.end;
          }
          return max;
        }, new Date(-8640000000000000));
        parentStart = new Date(childEarliestStart);
        parentEnd = new Date(childLatestEnd);
      }
      const grandParent =
        parent && parent.parentId ? items[parent.parentId] : null;
      let grandParentStart = grandParent ? grandParent.start : null;
      let grandParentEnd = grandParent ? grandParent.end : null;
      if (grandParent) {
        if (newStart < grandParent.start) grandParentStart = new Date(newStart);
        if (newEnd > grandParent.end) grandParentEnd = new Date(newEnd);
        const parentEarliestStart = Object.values(items).reduce((min, item) => {
          if (
            item.type === "task" &&
            item.parentId === grandParent.id &&
            item.start < min
          ) {
            return item.start;
          }
          return min;
        }, new Date(8640000000000000));
        const parentLatestEnd = Object.values(items).reduce((max, item) => {
          if (
            item.type === "task" &&
            item.parentId === grandParent.id &&
            item.end > max
          ) {
            return item.end;
          }
          return max;
        }, new Date(-8640000000000000));
        grandParentStart = new Date(parentEarliestStart);
        grandParentEnd = new Date(parentLatestEnd);
      }

      setItems((prev) => ({
        ...prev,
        [it.id]: { ...it, start: newStart, end: newEnd },
        ...(parent
          ? { [parent.id]: { ...parent, start: parentStart, end: parentEnd } }
          : {}),
        ...(grandParent
          ? {
              [grandParent.id]: {
                ...grandParent,
                start: grandParentStart,
                end: grandParentEnd,
              },
            }
          : {}),
        ...childToEdit.reduce((acc, c) => {
          acc[c.id] = { ...prev[c.id], start: c.start, end: c.end };
          return acc;
        }, {}),
        ...grandChildToEdit.reduce((acc, c) => {
          acc[c.id] = { ...prev[c.id], start: c.start, end: c.end };
          return acc;
        }, {}),
      }));
    };
    const onUp = async () => {
      if (!dragState) return;
      const it = items[dragState.id];
      const grandChildren = dragState.grandChildren || [];
      const children = dragState.children || [];
      const parent = it.parentId ? items[it.parentId] : null;
      const grandParent =
        parent && parent.parentId ? items[parent.parentId] : null;
      setDragState(null);
      if (!it) return;
      // Persist by type
      try {
        if (it.type === "group") {
          const gid = it.details.originalId;
          await taskApi.updateTaskGroup(gid, {
            start_date: formatISODate(it.start),
            end_date: formatISODate(it.end),
          });
        } else if (it.type === "task") {
          const tid = it.details.originalId;
          await taskApi.updateTask(tid, {
            start_date: formatISODate(it.start),
            end_date: formatISODate(it.end),
          });
        } else if (it.type === "subtask") {
          const sid = it.details.originalId;
          await taskApi.updateSubtask(sid, {
            start_date: formatISODate(it.start),
            end_date: formatISODate(it.end),
          });
        }
        // also persist parent and grandparent if changed
        if (parent) {
          const pType = parent.type;
          const pOriginalId = parent.details.originalId;
          if (pType === "task") {
            await taskApi.updateTask(pOriginalId, {
              start_date: formatISODate(parent.start),
              end_date: formatISODate(parent.end),
            });
          } else if (pType === "taskGroup") {
            await taskApi.updateTaskGroup(pOriginalId, {
              start_date: formatISODate(parent.start),
              end_date: formatISODate(parent.end),
            });
          }
        }
        if (grandParent) {
          const gOriginalId = grandParent.details.originalId;
          await taskApi.updateTaskGroup(gOriginalId, {
            start_date: formatISODate(grandParent.start),
            end_date: formatISODate(grandParent.end),
          });
        }

        // also persist children and grandchildren if changed
        for (const c of children) {
          const childItem = items[c.id];
          const cOriginalId = childItem.details.originalId;
          const cType = childItem.type;
          if (cType === "task") {
            await taskApi.updateTask(cOriginalId, {
              start_date: formatISODate(childItem.start),
              end_date: formatISODate(childItem.end),
            });
          } else if (cType === "subtask") {
            await taskApi.updateSubtask(cOriginalId, {
              start_date: formatISODate(childItem.start),
              end_date: formatISODate(childItem.end),
            });
          }
        }
        for (const c of grandChildren) {
          const grandChildItem = items[c.id];
          const gcOriginalId = grandChildItem.details.originalId;
          await taskApi.updateSubtask(gcOriginalId, {
            start_date: formatISODate(grandChildItem.start),
            end_date: formatISODate(grandChildItem.end),
          });
        }
      } catch (e) {
        console.error("Persist failed, reverting", e);
        // revert on failure
        setItems((prev) => ({
          ...prev,
          [dragState.id]: {
            ...prev[dragState.id],
            start: dragState.origStart,
            end: dragState.origEnd,
          },
        }));
      }
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragState, pxPerDay, items]);

  // Actions
  const toggleExpand = (id) => {
    setItems((prev) => ({
      ...prev,
      [id]: { ...prev[id], expanded: !prev[id].expanded },
    }));
  };

  const handleDelete = async (id) => {
    const it = items[id];
    if (!it) return;
    // if (!confirm(`Delete ${it.type} "${it.label}"?`)) return;
    try {
      if (it.type === "group") {
        await taskApi.deleteTaskGroup(it.details.originalId);
        setItems((prev) => {
          const copy = { ...prev };
          const toDelete = [id];
          // delete descendants
          const collect = (pid) => {
            for (const k of Object.keys(copy)) {
              if (copy[k].parentId === pid) {
                toDelete.push(k);
                collect(k);
              }
            }
          };
          collect(id);
          for (const k of toDelete) delete copy[k];
          return copy;
        });
        setRootIds((prev) => prev.filter((x) => x !== id));
      } else if (it.type === "task") {
        await taskApi.deleteTask(it.details.originalId);
        setItems((prev) => {
          const copy = { ...prev };
          const toDelete = [id];
          const collect = (pid) => {
            for (const k of Object.keys(copy)) {
              if (copy[k].parentId === pid) {
                toDelete.push(k);
                collect(k);
              }
            }
          };
          collect(id);
          for (const k of toDelete) delete copy[k];
          return copy;
        });
      } else if (it.type === "subtask") {
        await taskApi.deleteSubtask(it.details.originalId);
        setItems((prev) => {
          const copy = { ...prev };
          delete copy[id];
          return copy;
        });
      }
    } catch (e) {
      console.error(e);
      alert("Delete failed.");
    }
  };

  const handleToggleSubtask = async (id) => {
    const it = items[id];
    if (!it || it.type !== "subtask") return;
    const newVal = !it.details.is_completed;
    setItems((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        status: newVal ? "Complete" : "Ongoing",
        progress: newVal ? 100 : 0,
        details: { ...prev[id].details, is_completed: newVal },
      },
    }));
    try {
      await taskApi.updateSubtask(it.details.originalId, {
        is_completed: newVal,
      });
    } catch (e) {
      console.error(e);
      // revert
      setItems((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          status: it.details.is_completed ? "Complete" : "Ongoing",
          progress: it.details.is_completed ? 100 : 0,
          details: { ...prev[id].details },
        },
      }));
      alert("Failed to update subtask.");
    }
  };

  // Add modals
  const AddModal = () => {
    const cfg = showAddModal;
    const [name, setName] = useState("");
    const [start, setStart] = useState(formatISODate(new Date()));
    const [end, setEnd] = useState(formatISODate(new Date()));
    const [priority, setPriority] = useState(2);

    if (!cfg) return null;
    const parent = cfg.parentId ? items[cfg.parentId] : null;

    const onSubmit = async (e) => {
      e.preventDefault();
      try {
        if (cfg.type === "group") {
          const site = Number(localStorage.getItem("site"));
          const payload = {
            site_id: site,
            task_group_name: name || "New Group",
            status: "Active",
            start_date: start,
            end_date: end,
            priority: Number(priority) || 2,
          };
          const res = await taskApi.addTaskGroup(payload);
          const g = res?.data || res; // API returns {status,message,data}? handle both
          const id = `group_${
            g?.id || g?.data?.id || Math.random().toString(36).slice(2)
          }`;
          const newItem = {
            id,
            label: g?.group_name || payload.task_group_name,
            type: "group",
            parentId: null,
            start: parseISODateLocal(g?.start_date || start),
            end: parseISODateLocal(g?.end_date || end),
            status: g?.status || "Active",
            progress: 0,
            expanded: true,
            details: { type: "taskGroup", originalId: g?.id },
          };
          setItems((prev) => ({ ...prev, [id]: newItem }));
          setRootIds((prev) => [...prev, id]);
        }
        if (cfg.type === "task" && parent?.type === "group") {
          const payload = {
            task_group: parent.details.originalId,
            task_name: name || "New Task",
            task_details: "–",
            task_status: "Not Started",
            start_date: start,
            end_date: end,
            priority: Number(priority) || 2,
            estimated_amount: "0",
          };
          const res = await taskApi.addTask(payload);
          const t = res?.data || res;
          const id = `task_${
            t?.id || t?.data?.id || Math.random().toString(36).slice(2)
          }`;
          const newItem = {
            id,
            label: t?.task_name || payload.task_name,
            type: "task",
            parentId: parent.id,
            start: parseISODateLocal(t?.start_date || start),
            end: parseISODateLocal(t?.end_date || end),
            status: t?.task_status || "Not Started",
            progress: 0,
            expanded: true,
            details: {
              type: "task",
              originalId: t?.id,
              task_group: payload.task_group,
            },
          };
          setItems((prev) => ({ ...prev, [id]: newItem }));
        }
        if (cfg.type === "subtask" && parent?.type === "task") {
          const payload = {
            task_id: parent.details.originalId,
            subtask_name: name || "New Subtask",
            subtask_details: "–",
            end_date: end, // API needs only end_date; we also handle start support when updating later
            priority: Number(priority) || 2,
            assign_to: [],
          };
          const res = await taskApi.addSubtask(payload);
          const s = res?.data || res;
          const id = `subtask_${
            s?.id || s?.data?.id || Math.random().toString(36).slice(2)
          }`;
          const newItem = {
            id,
            label: s?.subtask_name || payload.subtask_name,
            type: "subtask",
            parentId: parent.id,
            start: parseISODateLocal(start),
            end: parseISODateLocal(s?.end_date || end),
            status: "Ongoing",
            progress: 0,
            expanded: true,
            details: {
              type: "subtask",
              originalId: s?.id,
              is_completed: false,
            },
          };
          setItems((prev) => ({ ...prev, [id]: newItem }));
        }
        setShowAddModal(null);
      } catch (e) {
        console.error(e);
        alert("Failed to add.");
      }
    };

    return (
      <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
        <form
          onSubmit={onSubmit}
          className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 space-y-4"
        >
          <div className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            <h3 className="text-lg font-semibold">Add {cfg.type}</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-sm font-medium">Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-800"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">Start</label>
              <input
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-800"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">End</label>
              <input
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-800"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-800"
              >
                <option value={1}>Low</option>
                <option value={2}>Medium</option>
                <option value={3}>High</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setShowAddModal(null)}
              className="px-4 py-2 rounded-xl border border-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-xl bg-gray-900 text-white"
            >
              Add
            </button>
          </div>
        </form>
      </div>
    );
  };

  // Row height, header height
  const rowHeight = 48;
  const headerHeight = 48;

  // Fixed column widths for proper alignment
  const markerColumnWidth = 50;
  const nameColumnWidth = 200;
  const statusColumnWidth = 80;
  const dateColumnWidth = 105;
  const actionsColumnWidth = 100;

  // Visible rows for layout
  // const rows = rowOrder.map(({ id, level }, idx) => ({
  //   ...items[id],
  //   id,
  //   level,
  //   top: idx * rowHeight,
  // }));
  const rows = useMemo(() => {
    return rowOrder.map(({ id, level }, idx) => ({
      ...items[id],
      id,
      level,
      top: idx * rowHeight,
    }));
  }, [rowOrder, items]);

  // X position helpers
  const xForDate = (d) => Math.round(((d - minDate) / MS_PER_DAY) * pxPerDay);
  const wForSpan = (s, e) => Math.max(8, daysBetween(s, e) * pxPerDay);

  const monthTicks = useMemo(() => {
    const out = [];
    let d = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    while (d <= maxDate) {
      out.push(new Date(d));
      d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    }
    return out;
  }, [minDate, maxDate]);

  const dayTicks = useMemo(() => {
    const out = [];
    let d = new Date(minDate);
    while (d <= maxDate) {
      out.push(new Date(d));
      d = new Date(d.getTime() + MS_PER_DAY);
    }
    return out;
  }, [minDate, maxDate]);
  const weekTicks = useMemo(() => {
    // start weeks on Monday
    const first = new Date(minDate);
    const deltaToMon = (first.getDay() + 6) % 7; // 0=Sun => 6
    first.setDate(first.getDate() - deltaToMon);
    const out = [];
    for (
      let d = new Date(first);
      d <= maxDate;
      d = new Date(d.getTime() + 7 * MS_PER_DAY)
    ) {
      out.push(new Date(d));
    }
    return out;
  }, [minDate, maxDate]);

  const quarterTicks = useMemo(() => {
    const out = [];
    let y = minDate.getFullYear();
    let m = Math.floor(minDate.getMonth() / 3) * 3;
    for (let d = new Date(y, m, 1); d <= maxDate; ) {
      out.push(new Date(d));
      m += 3;
      d = new Date(d.getFullYear(), m, 1);
    }
    return out;
  }, [minDate, maxDate]);

  const tickMode = useMemo(() => {
    // Tune thresholds as you like
    if (pxPerDay >= 16) return "day";
    if (pxPerDay >= 8) return "week";
    if (pxPerDay >= 3) return "month";
    return "quarter";
  }, [pxPerDay]);

  // Zoom presets for selector
  const zoomPresetPx = useMemo(
    () => ({
      day: 24,
      week: 10,
      month: 6,
      quarter: 2,
    }),
    [],
  );

  const gridTicks = useMemo(() => {
    switch (tickMode) {
      case "day":
        return dayTicks;
      case "week":
        return weekTicks;
      case "month":
        return monthTicks;
      case "quarter":
        return quarterTicks;
      default:
        return dayTicks;
    }
  }, [tickMode, dayTicks, weekTicks, monthTicks, quarterTicks]);

  const centerOnToday = () => {
    if (!rightRef.current) return;
    const today = new Date();
    const todayMidnight = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    if (todayMidnight < minDate || todayMidnight > maxDate) return;
    const todayX = xForDate(todayMidnight);
    const container = rightRef.current;
    const containerWidth = container.clientWidth;
    const scrollLeft = todayX - containerWidth / 2;
    container.scrollLeft = Math.max(0, scrollLeft);
  };

  const fitToContent = () => {
    const days = daysBetween(minDate, maxDate);
    const target = clamp(Math.round(1200 / days), minPxPerDay, maxPxPerDay);
    setPxPerDay(target);

    // Wait for re-render with new pxPerDay, then center on today
    setTimeout(() => {
      if (!rightRef.current) return;

      const today = new Date();
      const todayMidnight = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
      );
      if (todayMidnight < minDate || todayMidnight > maxDate) return;

      const todayX = xForDate(todayMidnight);
      const container = rightRef.current;
      const containerWidth = container.clientWidth;

      // Center today in the visible area
      const scrollLeft = todayX - containerWidth / 2;

      container.scrollLeft = Math.max(0, scrollLeft);
    }, 0);
  };

  // Auto-fit and center to "today" after data is ready (initial mount, tab switch, or manual reload)
  useEffect(() => {
    if (
      !loading &&
      rows.length > 0 &&
      rightRef.current &&
      !didAutoFitRef.current
    ) {
      fitToContent();
      didAutoFitRef.current = true;
    }
  }, [loading, rows.length, minDate, maxDate]); // run when data bounds/rows stabilize

  return (
    <div className="w-full h-full flex flex-col px-2 rounded-lg">
      {/* Toolbar */}
      <div className="flex items-center gap-2 p-3 rounded-t-lg border-b border-gray-200  sticky top-0 z-10">
        <div className="ml-auto flex items-center gap-2">
          <button
            className="px-2 py-2 rounded-xl border"
            onClick={() =>
              setPxPerDay((p) => clamp(p - 2, minPxPerDay, maxPxPerDay))
            }
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          {/* Zoom range selector */}
          <div className="flex items-center rounded-xl overflow-hidden border">
            {[
              { key: "day", label: "Daily" },
              { key: "week", label: "Weekly" },
              { key: "month", label: "Monthly" },
              { key: "quarter", label: "Quarterly" },
            ].map((opt, idx) => {
              const active = tickMode === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => {
                    setPxPerDay(zoomPresetPx[opt.key]);
                    setTimeout(centerOnToday, 0);
                  }}
                  className={`px-3 py-2 text-sm ${
                    active
                      ? "bg-[#7E22CE] text-white"
                      : "bg-white text-gray-700"
                  } ${idx !== 0 ? "border-l" : ""}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <button
            className="px-2 py-2 rounded-xl border"
            onClick={() =>
              setPxPerDay((p) => clamp(p + 2, minPxPerDay, maxPxPerDay))
            }
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button
            className="px-3 py-1 rounded-xl border"
            onClick={fitToContent}
          >
            Today
          </button>
          <button className="px-3 py-1 rounded-xl border" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-1 inline" /> Reload
          </button>
        </div>
      </div>

      {/* Grid container */}
      <div className="flex-1 min-h-0 h-full grid grid-cols-[435px,1fr]">
        {/* Left list */}
        <div
          className="border-r border-gray-200 bg-white overflow-auto"
          ref={leftRef}
          style={{
            scrollbarWidth: "none",
            msScrollbarBaseColor: "transparent",
            msScrollbarTrackColor: "transparent",
          }}
        >
          {/* Header - Fixed positioning with exact column widths */}
          <div className="sticky top-0 z-10 rounded-tl-lg bg-white border-b w-[435px] border-gray-200">
            <div
              className="flex items-center rounded-tl-lg text-sm font-medium text-gray-600 tracking-wide"
              style={{
                height: `${headerHeight}px`,
                // paddingLeft: "12px",
                // paddingRight: "12px",
                width: "100%",
              }}
            >
              <div
                style={{ width: `${markerColumnWidth}px` }}
                className="flex flex-col rounded-tl-lg border-[1px] h-full text-center justify-center items-center border-[#78797A]"
              >
                <input type="checkbox" disabled className="w-4 h-4" />
              </div>
              <div
                style={{ width: `${nameColumnWidth}px` }}
                className="flex flex-col border-y-[1px] h-full text-center justify-center items-center border-[#78797A]"
              >
                Task groups
              </div>
              <div
                style={{ width: `${statusColumnWidth}px` }}
                className="flex flex-col border-y-[1px] border-l-[1px] h-full text-center justify-center items-center border-[#78797A]"
              >
                Status
              </div>
              <div
                style={{ width: `${dateColumnWidth}px` }}
                className="flex flex-col border-[1px] h-full text-center justify-center items-center border-[#78797A]"
              >
                Due Date
              </div>
              {/* <div
                style={{ width: `${actionsColumnWidth}px` }}
                className="flex-shrink-0"
              >
                Actions
              </div> */}
            </div>
          </div>

          {/* Rows */}
          <div
            className="relative bg-white h-full"
            // style={{ height: rows.length * rowHeight }}
          >
            {rows.map((r, idx) => (
              <>
                {r.details.type === "task" && r.expanded && (
                  <>
                    {Object.values(items)
                      .filter((row) => row.parentId === r.id)
                      .map((subRow) => (
                        <Xarrow
                          key={subRow.id}
                          start={r.id}
                          end={subRow.id}
                          startAnchor="bottom"
                          endAnchor="left"
                          strokeWidth={2}
                          color="#3B0069"
                          zIndex={4}
                          path="grid"
                          headSize={0}
                        />
                      ))}
                  </>
                )}
                <div
                  key={r.id}
                  className={` flex flex-row items-center ${
                    selectedId === r.id ? "bg-gray-50" : ""
                  } ${
                    r.details.type === "taskGroup"
                      ? "border-b-[1px] border-[#78797A]"
                      : ""
                  }`}
                  style={{
                    top: r.top,
                    height: `${rowHeight}px`,
                    //   paddingLeft: "12px",
                    // paddingRight: "12px",
                  }}
                >
                  {r.details.type === "task" && r.parentId && (
                    <Xarrow
                      start={r.parentId}
                      end={r.id}
                      startAnchor="bottom"
                      endAnchor="left"
                      strokeWidth={2}
                      color="#3B0069"
                      zIndex={4}
                      path="grid"
                      headSize={0}
                    />
                  )}
                  {r.details.type === "taskGroup" &&
                    r.taskGroupOrder > 0 &&
                    rows.find((row) => row.id === r.previous_group_id)
                      ?.expanded && (
                      <Xarrow
                        start={r.previous_group_id}
                        end={r.id}
                        startAnchor="bottom"
                        endAnchor="top"
                        strokeWidth={2}
                        color="#3B0069"
                        zIndex={4}
                        path="grid"
                        headSize={0}
                      />
                    )}
                  {r.details.type === "task" &&
                    r.task_order > 0 &&
                    rows.find((row) => row.id === r.previous_task_id)
                      ?.expanded && (
                      <Xarrow
                        start={r.previous_task_id}
                        end={r.id}
                        startAnchor="bottom"
                        endAnchor="top"
                        strokeWidth={2}
                        color="#3B0069"
                        zIndex={4}
                        path="grid"
                        headSize={0}
                      />
                    )}

                  {/* Marker column - exact width match with header */}
                  <div
                    className={`flex flex-col border-l-[1px] ${
                      r.details.type === "taskGroup" &&
                      r.taskGroupOrder > 0 &&
                      rows.find((row) => row.id === r.previous_group_id)
                        ?.expanded
                        ? "border-t-[1px]"
                        : ""
                    } border-[#78797A] items-center justify-center flex-shrink-0`}
                    style={{
                      width: `${markerColumnWidth}px`,
                      height: `${rowHeight}px`,
                    }}
                    id={r.details.type === "taskGroup" ? r.id : undefined}
                  >
                    {r.details.type === "taskGroup" ? (
                      <input type="checkbox" disabled className="w-4 h-4" />
                    ) : null}
                  </div>
                  <div
                    className={`flex flex-col items-center 
                    ${
                      r.details.type === "task"
                        ? "border-l-[1px] border-b-[1px] border-[#78797A]"
                        : ""
                    } 
                    ${
                      r.details.type === "subtask" &&
                      r.type !== "subtaskHeader" &&
                      !rows.find((row) => row.previous_subtask_id === r.id)
                        ? "border-b-[1px] border-[#78797A]"
                        : ""
                    }
                    justify-center flex-shrink-0`}
                    style={{
                      width: `${markerColumnWidth}px`,
                      height: `${rowHeight}px`,
                      borderBottomLeftRadius: r.expanded ? 10 : 0,
                      display:
                        r.details.type === "task" ||
                        r.details.type === "subtask"
                          ? "flex"
                          : "none",
                    }}
                    id={r.details.type === "task" ? r.id : undefined}
                  >
                    {r.details.type === "task" && (
                      <input type="checkbox" disabled className="w-4 h-4" />
                    )}
                  </div>
                  <div
                    className={`flex flex-col items-center ${
                      r.details.type === "subtask"
                        ? "border-l-[1px] border-b-[1px] border-[#78797A]"
                        : ""
                    } 

                justify-center flex-shrink-0`}
                    style={{
                      width: `${markerColumnWidth}px`,
                      height: `${rowHeight}px`,
                      borderBottomLeftRadius: r.expanded ? 10 : 0,
                      display: r.details.type === "subtask" ? "flex" : "none",
                    }}
                    id={r.details.type === "subtask" ? r.id : undefined}
                  >
                    {r.details.type === "subtask" && (
                      <input type="checkbox" disabled className="w-4 h-4" />
                    )}
                  </div>
                  {/* Name column - exact width match with header */}
                  <div
                    className={`flex items-center gap-2 border-[#78797A] flex-shrink-0 
                    ${
                      r.details.type === "subtask"
                        ? "border-l-[1px]"
                        : "border-x-[1px]"
                    }
                    ${
                      r.details.type === "taskGroup"
                        ? ""
                        : "border-b-[1px] border-b-[#78797A]"
                    }`}
                    style={{
                      width: `${
                        nameColumnWidth -
                        (r.details.type === "taskGroup"
                          ? 0
                          : r.details.type === "task"
                            ? 50
                            : 100) +
                        (r.details.type === "subtask" ? statusColumnWidth : 0)
                      }px`,
                      paddingLeft: r.level * 4,
                      height: `${rowHeight}px`,
                    }}
                  >
                    <button
                      onClick={() => toggleExpand(r.id)}
                      className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 flex-shrink-0"
                      style={{
                        display: r.details.type === "subtask" ? "none" : "flex",
                      }}
                    >
                      {r.expanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>

                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {/* Checkbox for subtasks */}
                      {/* {r.type === "subtask" && (
                      <button
                        onClick={() => handleToggleSubtask(r.id)}
                        className="flex-shrink-0"
                      >
                        {r.details?.is_completed ? (
                          <CheckSquare className="w-4 h-4" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                      </button>
                    )} */}

                      {/* Task name with ellipsis */}
                      <button
                        onClick={() => setSelectedId(r.id)}
                        className={`min-w-0 flex-1 ${
                          r.type === "subtaskHeader"
                            ? "text-center"
                            : "text-left"
                        }`}
                      >
                        <div
                          className=" text-black text-sm truncate"
                          title={r.label}
                        >
                          {r.label}
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Status column - exact width match with header */}
                  <div
                    className={`flex-shrink-0 ${
                      r.details.type === "task"
                        ? "border-b-[1px] border-[#78797A]"
                        : ""
                    }`}
                    style={{
                      width: `${statusColumnWidth}px`,
                      height: `${rowHeight}px`,
                      display: r.details.type === "subtask" ? "none" : "flex",
                    }}
                  >
                    <div className="flex flex-col items-center h-full w-full justify-center">
                      {r.type === "taskHeader" ? (
                        <span className={`text-sm text-center px-2 py-1`}>
                          Status
                        </span>
                      ) : (
                        // <span
                        //   className={`text-xs text-center px-2 py-1 rounded-full border inline-block truncate max-w-full ${statusColor(
                        //     r.status
                        //   )}`}
                        // >
                        //   {r.status || "—"}
                        // </span>
                        <span className="text-xs text-center px-1 w-full">
                          <StatusBadge value={r.status} />
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Due date column - exact width match with header */}
                  <div
                    className={`flex flex-col text-sm items-center justify-center ${
                      r.details.type === "taskGroup" ? "" : "border-b-[1px]"
                    } border-l-[1px] border-[#78797A] text-black`}
                    style={{
                      width: `${dateColumnWidth}px`,
                      height: `${rowHeight}px`,
                    }}
                  >
                    <div className="flex flex-col items-center">
                      {r.type === "taskHeader" || r.type === "subtaskHeader"
                        ? "Due Date"
                        : formatISODate(r.end)}
                    </div>
                  </div>

                  {/* Actions - exact width match with header */}
                  {/* <div
                  className="flex items-center gap-1 flex-shrink-0"
                  style={{ width: `${actionsColumnWidth}px` }}
                >
                  {r.type === "group" && (
                    <button
                      onClick={() =>
                        setShowAddModal({ type: "task", parentId: r.id })
                      }
                      className="px-2 py-1 text-xs rounded-lg border whitespace-nowrap"
                    >
                      + Task
                    </button>
                  )}
                  {r.type === "task" && (
                    <button
                      onClick={() =>
                        setShowAddModal({ type: "subtask", parentId: r.id })
                      }
                      className="px-2 py-1 text-xs rounded-lg border whitespace-nowrap"
                    >
                      + Subtask
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(r.id)}
                    className="p-1 rounded-lg hover:bg-red-50 flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div> */}
                </div>
              </>
            ))}
          </div>
        </div>

        {/* Right timeline */}
        <div className="overflow-auto relative h-full" ref={rightRef}>
          {/* Header with months and days */}
          <div
            className="sticky top-0 z-10 bg-white border-[1px] border-[#78797A]"
            style={{
              height: headerHeight,
              minWidth: "100%",
              width: Math.max(contentWidth + 10, 0),
            }}
          >
            <div
              className="relative"
              style={{
                minWidth: "100%",
                width: Math.max(contentWidth + 10, 0),
              }}
            >
              {/* Month labels */}
              {/* Top scale: Months or Quarters (when very zoomed out) */}
              <div className="absolute left-0 top-0 h-6 w-full text-[11px] text-gray-700">
                {(tickMode === "quarter" ? quarterTicks : monthTicks).map(
                  (d, i, arr) => {
                    const x = xForDate(d);
                    const next = arr[i + 1] || maxDate;
                    const w = xForDate(next) - x;
                    const label =
                      tickMode === "quarter"
                        ? `Q${
                            Math.floor(d.getMonth() / 3) + 1
                          } ${d.getFullYear()}`
                        : d.toLocaleString(undefined, {
                            month: "short",
                            year: "numeric",
                          });
                    return (
                      <div
                        key={i}
                        className="absolute h-6 border-r border-gray-200 flex items-center px-2"
                        style={{ left: x, width: w }}
                      >
                        <span>{label}</span>
                      </div>
                    );
                  },
                )}
              </div>

              {/* Day grid under months */}
              {/* Secondary scale under the top row */}
              <div className="absolute left-0 top-6 h-6 w-full text-[10px] text-gray-500">
                {tickMode === "day" &&
                  dayTicks.map((d, i) => {
                    const x = xForDate(d);
                    return (
                      <div
                        key={`day-${i}`}
                        className="absolute top-0 h-6 border-r border-gray-100 text-center"
                        style={{ left: x, width: pxPerDay }}
                      >
                        <span className="inline-block mt-[2px] w-full">
                          {d.getDate()}
                        </span>
                      </div>
                    );
                  })}

                {tickMode === "week" &&
                  weekTicks.map((d, i) => {
                    const x = xForDate(d);
                    const w = 7 * pxPerDay;
                    const end = new Date(d.getTime() + 6 * MS_PER_DAY);
                    const label = `${d.getDate()}–${end.getDate()}`;
                    return (
                      <div
                        key={`wk-${i}`}
                        className="absolute top-0 h-6 border-r border-gray-100 text-center"
                        style={{ left: x, width: w }}
                      >
                        <span className="inline-block mt-[2px] w-full">
                          {label}
                        </span>
                      </div>
                    );
                  })}

                {/* For 'month' and 'quarter', we keep only the top row to avoid clutter */}
              </div>
            </div>
          </div>

          {/* {rows.map((r) => (
            <>
              {r.details.type === "task" && r.parentId && (
                <Xarrow
                  start={r.parentId}
                  end={r.id}
                  startAnchor="bottom"
                  endAnchor="left"
                  strokeWidth={2}
                  color="#3B0069"
                  zIndex={10}
                  path="grid"
                  headSize={0}
                />
              )}
            </>
          ))} */}
          {/* Rows backdrop grid */}
          <div
            className="relative h-full"
            style={{ minWidth: "100%", width: Math.max(contentWidth, 0) }}
          >
            {/* Vertical day lines */}
            <svg
              width={Math.max(contentWidth, 0)}
              height={Math.max(rows.length, 11) * rowHeight}
              className="absolute left-0 top-0"
              style={{ minWidth: "100%" }}
            >
              {/* Today highlight */}
              {(() => {
                const today = new Date();
                const t0 = new Date(
                  today.getFullYear(),
                  today.getMonth(),
                  today.getDate(),
                );
                if (t0 >= minDate && t0 <= maxDate) {
                  const x = xForDate(t0);
                  return (
                    <line
                      x1={x}
                      y1={0}
                      x2={x}
                      y2={Math.max(rows.length, 11) * rowHeight}
                      stroke="#111827"
                      strokeWidth={1}
                      strokeDasharray="4 4"
                    />
                  );
                }
                return null;
              })()}

              {gridTicks.map((d, i) => (
                <line
                  key={`grid-${i}`}
                  x1={xForDate(d)}
                  y1={0}
                  opacity={0.7}
                  x2={xForDate(d)}
                  y2={Math.max(rows.length, 11) * rowHeight}
                  stroke="#E0E4E6"
                />
              ))}

              {/* Define arrowhead marker in SVG defs */}
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="10"
                  markerHeight="10"
                  refX="9"
                  refY="3"
                  orient="auto"
                  markerUnits="strokeWidth"
                >
                  <path d="M0,0 L0,6 L9,3 z" fill="#3B0069" />
                </marker>
              </defs>

              {rows.map((r) => {
                if (r.type === "taskHeader" || r.type === "subtaskHeader") {
                  return null;
                }

                // Task to parent task group arrow
                if (r.details.type === "task" && r.parentId) {
                  const parentItem = items[r.parentId];
                  if (parentItem && parentItem.expanded) {
                    const parentX = xForDate(parentItem.start);
                    const parentRow = rows.find((row) => row.id === r.parentId);
                    if (!parentRow) return null;

                    const parentY = parentRow.top + rowHeight / 2;
                    const currentX = xForDate(r.start);
                    const currentY = r.top + rowHeight / 2;

                    // Grid path: right from parent, down, then right to current
                    const midX = parentX - 50;
                    const arrowPath = `M ${parentX} ${parentY} L ${midX} ${parentY} L ${midX} ${currentY} L ${currentX} ${currentY}`;

                    return (
                      <path
                        key={`arrow-${r.id}`}
                        d={arrowPath}
                        stroke="#3B0069"
                        strokeWidth={2}
                        fill="none"
                        markerEnd="url(#arrowhead)"
                      />
                    );
                  }
                }

                if (r.details.type === "subtask" && r.parentId) {
                  const parentItem = items[r.parentId];
                  if (parentItem && parentItem.expanded) {
                    const parentX =
                      (xForDate(parentItem.end) + xForDate(parentItem.start)) /
                      2;
                    const parentRow = rows.find((row) => row.id === r.parentId);
                    if (!parentRow) return null;
                    const parentY = parentRow.top + rowHeight;
                    const currentX = xForDate(r.start);
                    const currentY = r.top + rowHeight / 2;
                    // Grid path: right from parent, down, then right to current
                    const midX = xForDate(parentItem.start) - 40;
                    const arrowPath = `M ${parentX} ${
                      parentY - 10
                    } L ${parentX} ${parentY} L ${midX} ${parentY} L ${midX} ${currentY} L ${currentX} ${currentY}`;
                    return (
                      <path
                        key={`arrow-subtask-${r.id}`}
                        d={arrowPath}
                        stroke="#3B0069"
                        strokeWidth={2}
                        fill="none"
                        markerEnd="url(#arrowhead)"
                      />
                    );
                  }
                }

                return null;
              })}

              {/* Bars */}
              {rows.map((r) => {
                const x = xForDate(r.start);
                const w = wForSpan(r.start, r.end);
                const y = r.top + 8; // padding inside row
                const h = rowHeight - 16;
                const radius = 5;
                const fill =
                  r.type === "group"
                    ? "#3B0069"
                    : r.type === "task"
                      ? "#3B0069"
                      : "#3B0069D4";
                const progressW = (w * (r.progress || 0)) / 100;
                if (r.type === "taskHeader" || r.type === "subtaskHeader") {
                  return null;
                }
                const hasChild = Object.values(items).some(
                  (it) =>
                    it.parentId === r.id &&
                    it.type !== "subtaskHeader" &&
                    it.type !== "taskHeader",
                );

                return (
                  <g key={r.id} id={r.id}>
                    {/* bar bg */}
                    <rect
                      x={x}
                      y={y}
                      width={w}
                      height={h}
                      rx={radius}
                      ry={radius}
                      fill={fill}
                      opacity={0.42}
                      stroke="#e2e8f0"
                    />
                    {/* progress */}
                    <rect
                      x={x}
                      y={y}
                      width={progressW}
                      height={h}
                      rx={radius}
                      ry={radius}
                      fill={fill}
                      opacity={0.35}
                    />
                    {/* main bar */}
                    <rect
                      x={x}
                      y={y}
                      width={w}
                      height={h}
                      rx={radius}
                      ry={radius}
                      fill={fill}
                      opacity={0.6}
                      // onMouseDown={(e) => onBarMouseDown(e, r.id, "move")}
                      // className="cursor-grab"
                      onMouseDown={(e) => onBarMouseDown(e, r.id, "move")}
                      className="cursor-grab"
                    />
                    {/* task name inside bar */}
                    <foreignObject
                      x={x + 4}
                      y={y}
                      width={w - 8}
                      height={h}
                      style={{ pointerEvents: "none" }}
                    >
                      <div
                        xmlns="http://www.w3.org/1999/xhtml"
                        className="w-full h-full flex items-center px-2 overflow-hidden"
                      >
                        <span
                          className="text-xs text-white truncate"
                          style={{ pointerEvents: "none" }}
                        >
                          {r.label}
                        </span>
                      </div>
                    </foreignObject>
                    {/* resize handles */}
                    {hasChild || !hasTaskAuthority ? null : (
                      <>
                        {/* Left resize handle */}
                        <g
                          className="cursor-ew-resize"
                          onMouseDown={(e) =>
                            onBarMouseDown(e, r.id, "resize-left")
                          }
                        >
                          <rect
                            x={x - 6}
                            y={y + h * 0.2}
                            width={2.5}
                            height={h * 0.6}
                            fill={fill}
                            opacity={1}
                            rx={0}
                          />
                          <line
                            x1={x}
                            y1={y + h * 0.5}
                            x2={x - 5}
                            y2={y + h * 0.5}
                            stroke={fill}
                            strokeWidth={2}
                            strokeLinecap="round"
                            opacity={1}
                          />
                        </g>

                        {/* Right resize handle */}
                        <g
                          className="cursor-ew-resize"
                          onMouseDown={(e) =>
                            onBarMouseDown(e, r.id, "resize-right")
                          }
                        >
                          <rect
                            x={x + w + 5}
                            y={y + h * 0.2}
                            width={2.5}
                            height={h * 0.6}
                            fill={fill}
                            opacity={1}
                            // rx={2}
                          />
                          <line
                            x1={x + w}
                            y1={y + h * 0.5}
                            x2={x + w + 4}
                            y2={y + h * 0.5}
                            stroke={fill}
                            strokeWidth={2}
                            strokeLinecap="round"
                            opacity={1}
                          />
                        </g>
                      </>
                    )}
                  </g>
                );
              })}
            </svg>

            {/* Row separators */}
            {/* {rows.map((r) => (
              <div
                key={r.id}
                className="absolute left-0 right-0 border-b border-gray-200"
                style={{ top: r.top + rowHeight - 1, width:"fit-content" }}
              />
            ))} */}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showAddModal && <AddModal />}

      {/* Status */}
      {loading && (
        <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur rounded-xl shadow px-3 py-2 text-sm">
          Loading…
        </div>
      )}
      {error && (
        <div className="absolute bottom-4 right-4 bg-red-600 text-white rounded-xl shadow px-3 py-2 text-sm">
          {error}
        </div>
      )}
    </div>
  );
}
